import { useState, useEffect } from 'react';
import { useUserContext } from './userContext';

export interface OnboardingStep {
  step: number;
  name: string;
  isAccessible: boolean;
  isCompleted: boolean;
  requiredStatus: string;
}

export const useOnboardingValidation = () => {
  const { user } = useUserContext();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      step: 1,
      name: 'Plan Selection',
      isAccessible: true,
      isCompleted: false,
      requiredStatus: 'any'
    },
    {
      step: 2,
      name: 'Email & Password',
      isAccessible: true,
      isCompleted: false,
      requiredStatus: 'any'
    },
    {
      step: 3,
      name: 'Email Verification',
      isAccessible: false,
      isCompleted: false,
      requiredStatus: 'pending_email_verification'
    },
    {
      step: 4,
      name: 'Profile Information',
      isAccessible: false,
      isCompleted: false,
      requiredStatus: 'pending_profile_completion'
    }
  ]);

  useEffect(() => {
    if (!user) return;

    const userStatus = user.onboardingStatus || 'pending_email_verification';
    
    // Update step accessibility based on user status
    const updatedSteps = steps.map(step => {
      let isAccessible = false;
      let isCompleted = false;

      switch (step.step) {
        case 1:
        case 2:
          isAccessible = true;
          isCompleted = userStatus !== 'pending_email_verification';
          break;
        case 3:
          isAccessible = userStatus === 'pending_email_verification';
          isCompleted = userStatus === 'pending_profile_completion' || userStatus === 'active';
          break;
        case 4:
          isAccessible = userStatus === 'pending_profile_completion';
          isCompleted = userStatus === 'active';
          break;
      }

      return {
        ...step,
        isAccessible,
        isCompleted
      };
    });

    setSteps(updatedSteps);

    // Determine current step based on status
    let targetStep = 1;
    if (userStatus === 'pending_email_verification') {
      targetStep = 3;
    } else if (userStatus === 'pending_profile_completion') {
      targetStep = 4;
    } else if (userStatus === 'active') {
      targetStep = 5; // Dashboard
    }
    
    setCurrentStep(targetStep);
  }, [user]);

  const canAccessStep = (stepNumber: number): boolean => {
    const step = steps.find(s => s.step === stepNumber);
    return step ? step.isAccessible : false;
  };

  const isStepCompleted = (stepNumber: number): boolean => {
    const step = steps.find(s => s.step === stepNumber);
    return step ? step.isCompleted : false;
  };

  const getNextStep = (): number => {
    const currentStepData = steps.find(s => s.step === currentStep);
    if (!currentStepData) return 1;

    // Find the next incomplete step
    const nextStep = steps.find(s => s.step > currentStep && !s.isCompleted);
    return nextStep ? nextStep.step : currentStep;
  };

  const getRequiredStep = (): number => {
    if (!user) return 1;
    
    const userStatus = user.onboardingStatus || 'pending_email_verification';
    
    switch (userStatus) {
      case 'pending_email_verification':
        return 2; // Email/Password step
      case 'pending_profile_completion':
        return 4; // Address/Profile completion step
      case 'active':
        return 5; // Dashboard
      default:
        return 1;
    }
  };

  return {
    currentStep,
    steps,
    canAccessStep,
    isStepCompleted,
    getNextStep,
    getRequiredStep,
    userStatus: user?.onboardingStatus || 'pending_email_verification'
  };
};
